/**
 * doorLeaves.ts — proximity-animated sliding doors for hollow settlement
 * buildings (2026-07-30 "openable doors that auto-open/close on proximity").
 *
 * VISUAL-ONLY, the streetlamp-pool discipline: a fixed pool of leaf
 * assemblies (two pocket-sliding panels each), created ONCE and re-parented
 * to whichever planet's group hosts the nearby doors (Object3D reparenting is
 * cheap — unlike lights it triggers no shader recompiles). Collision stays
 * the open doorway: a closed leaf is cosmetic and never blocks a body, so the
 * walk-in contract (and every probe built on it) is untouched — the leaves
 * simply slide into the flank pockets before the player reaches the plane.
 *
 * Panels sit at the WALL CENTRE plane and slide along the wall into the
 * flanks (the door gap is 1.6 m; each 0.82 m panel retracts ±0.85 m, ending
 * fully inside the 1.2 m flank body — a pocket door, no swing arcs to clip).
 */
import * as THREE from "three";
import type { Planet } from "../planet";
import type { SettlementRenderer } from "./renderer";
import type { DoorPlacement } from "./buildingMeshes";

/** Pool 6 → 40, scan 26 → 130 m (2026-07-30 "doors load way too close"):
 *  distant doors just sit closed — same trivial per-frame cost — but the
 *  street no longer shows bare openings until you're on the doorstep. */
const POOL = 40;
/** Doors considered for animation (m). */
const SCAN_M = 130;
/** Fully open inside this range… */
const OPEN_M = 3.0;
/** …and fully closed beyond this (hysteresis band holds in between). */
const CLOSE_M = 4.0;
/** Panel slide travel (m): closed centre ±0.42 → open centre ±1.27. */
const TRAVEL_M = 0.85;

interface Leaf {
  group: THREE.Group;
  left: THREE.Mesh;
  right: THREE.Mesh;
  mat: THREE.MeshLambertMaterial;
  key: string | null;
  openK: number;
}

export class DoorLeaves {
  private readonly leaves: Leaf[] = [];
  private planet: Planet | null = null;

  constructor() {
    const geo = new THREE.BoxGeometry(0.82, 2.6, 0.09);
    const stripeGeo = new THREE.BoxGeometry(0.82, 0.14, 0.1);
    const stripeMat = new THREE.MeshLambertMaterial({ color: 0xb35c1e });
    for (let i = 0; i < POOL; i++) {
      const group = new THREE.Group();
      // Per-PAIR panel material (2026-07-30 "doors use the same colour system
      // as the house/floor"): pose() paints it with the door's floor-family
      // tint, so each pair matches its building's floors/stairs.
      const mat = new THREE.MeshLambertMaterial({ color: 0x4d5157 });
      const mk = (): THREE.Mesh => {
        const m = new THREE.Mesh(geo, mat);
        const s = new THREE.Mesh(stripeGeo, stripeMat);
        s.position.y = 0.55;
        m.add(s);
        return m;
      };
      const left = mk(), right = mk();
      group.add(left, right);
      group.visible = false;
      this.leaves.push({ group, left, right, mat, key: null, openK: 0 });
    }
  }

  /** Per-frame: animate leaves for the doors near the player. `local` is the
   *  player's PLANET-LOCAL position on `planet` (null when not on foot). */
  update(renderer: SettlementRenderer | null, planet: Planet | null,
    local: THREE.Vector3 | null, dt: number): void {
    if (planet !== this.planet) {
      for (const l of this.leaves) {
        l.group.parent?.remove(l.group);
        l.key = null;
        l.group.visible = false;
      }
      this.planet = planet;
      if (planet !== null) for (const l of this.leaves) planet.group.add(l.group);
    }
    if (renderer === null || planet === null || local === null) {
      for (const l of this.leaves) { l.key = null; l.group.visible = false; }
      return;
    }
    const near = renderer.doorsNear(planet, local.x, local.y, local.z, SCAN_M)
      .sort((a, b) => a.dist - b.dist)
      .slice(0, POOL);
    // Sticky slot assignment: keep leaves on their doors while still near.
    const wanted = new Map(near.map((n) => [n.key, n]));
    const freeSlots: Leaf[] = [];
    for (const l of this.leaves) {
      if (l.key !== null && wanted.has(l.key)) wanted.delete(l.key);
      else freeSlots.push(l);
    }
    for (const n of wanted.values()) {
      const l = freeSlots.pop();
      if (l === undefined) break;
      l.key = n.key;
      l.openK = 0;
      this.pose(l, n.d);
    }
    const ease = 1 - Math.exp(-9 * dt);
    for (const l of this.leaves) {
      if (l.key === null) { l.group.visible = false; continue; }
      const n = near.find((q) => q.key === l.key);
      if (n === undefined) { l.key = null; l.group.visible = false; continue; }
      l.group.visible = true;
      const target = n.dist < OPEN_M ? 1 : n.dist > CLOSE_M ? 0 : l.openK > 0.5 ? 1 : 0;
      l.openK += (target - l.openK) * ease;
      const off = 0.42 + TRAVEL_M * l.openK;
      l.left.position.x = -off;
      l.right.position.x = off;
    }
  }

  /** Place a leaf assembly at a doorway (planet-local frame). */
  private pose(l: Leaf, d: DoorPlacement): void {
    l.mat.color.setRGB(d.tint[0], d.tint[1], d.tint[2]);
    // Basis: x = slide axis (along wall), y = up, z = outward.
    const m = new THREE.Matrix4().makeBasis(
      new THREE.Vector3(d.ax, d.ay, d.az),
      new THREE.Vector3(d.ux, d.uy, d.uz),
      new THREE.Vector3(d.ox, d.oy, d.oz),
    );
    l.group.quaternion.setFromRotationMatrix(m);
    // Panels are 2.6 tall; centre them over the sill so the leaf spans the
    // opening (sill … sill+2.6, header at 3.2 — the top gap reads as transom).
    l.group.position.set(
      d.x + d.ux * (d.sillM + 1.3),
      d.y + d.uy * (d.sillM + 1.3),
      d.z + d.uz * (d.sillM + 1.3),
    );
  }
}
